/**
 * Aspect ratio switcher (SPEC §6.1).
 *
 * Computes a canvas size that fits within ~80% of the viewport while
 * preserving the requested aspect ratio, then drives an injected
 * Renderer / World / Typography adapter.
 *
 * This module is intentionally decoupled from concrete renderer/physics
 * implementations: Wave 4 (`src/app.ts`) wires the real Three.js renderer
 * and Verlet world via the {@link AspectAdapter} interface defined here.
 *
 * No `Math.random()`, no time-based behavior — `computeViewport` is pure
 * and deterministic given its inputs.
 */

import type { AspectId } from '../types.js';

/** Width:height ratio (long-edge / short-edge form is NOT used; raw w/h). */
function aspectRatioWH(aspect: AspectId): { w: number; h: number } {
  switch (aspect) {
    case '16x9':
      return { w: 16, h: 9 };
    case '9x16':
      return { w: 9, h: 16 };
    case '4x5':
      return { w: 4, h: 5 };
    case 'ipad11_portrait':
      // iPad Pro 11" (M2): 1668x2388
      return { w: 1668, h: 2388 };
    case 'ipad13_portrait':
      // iPad Pro 12.9" (M2): 2048x2732
      return { w: 2048, h: 2732 };
  }
}

export interface AspectViewport {
  /** Visible canvas size in CSS pixels. */
  width: number;
  height: number;
  /** Device pixel ratio used for rendering. */
  dpr: number;
}

export interface AspectAdapter {
  /** Update the rendering surface size. */
  setRenderSize(width: number, height: number, dpr: number): void;
  /** Update physics-world bounds (in pixels, matching the render size). */
  setWorldBounds(bounds: { width: number; height: number }): void;
  /** Re-layout typography (called after size changes). */
  relayoutTypography(): void;
}

export interface AspectComputeOpts {
  viewportWidth: number;
  viewportHeight: number;
  /** Fraction of the viewport's smaller dimension we're allowed to fill. Default 0.8. */
  fillFactor?: number;
  /** Device pixel ratio cap. Default min(devicePixelRatio, 2). */
  maxDpr?: number;
}

/**
 * Resolve the device pixel ratio used for rendering. Capped to keep the
 * backing-store cost bounded even on Retina-class displays.
 */
function resolveDpr(maxDpr?: number): number {
  if (typeof maxDpr === 'number' && Number.isFinite(maxDpr) && maxDpr > 0) {
    return maxDpr;
  }
  const devicePxRatio =
    typeof globalThis !== 'undefined' &&
    typeof (globalThis as { devicePixelRatio?: number }).devicePixelRatio === 'number'
      ? (globalThis as { devicePixelRatio: number }).devicePixelRatio
      : 1;
  return Math.min(Math.max(devicePxRatio, 1), 2);
}

/**
 * Compute the largest CSS-pixel `(width, height)` matching `aspect` that
 * fits inside `fillFactor * viewport`.
 *
 * Pure / deterministic. Rounding: `Math.floor` (chosen over banker's
 * rounding to guarantee we never exceed the budget by ±0.5px and to keep
 * results identical across browsers).
 */
export function computeViewport(aspect: AspectId, opts: AspectComputeOpts): AspectViewport {
  const fill = opts.fillFactor ?? 0.8;
  const ratio = aspectRatioWH(aspect);
  const maxW = Math.max(0, opts.viewportWidth * fill);
  const maxH = Math.max(0, opts.viewportHeight * fill);

  // Largest (w, h) with w/h == ratio.w/ratio.h such that w<=maxW and h<=maxH.
  // i.e. w = min(maxW, maxH * ratio.w/ratio.h)
  const wIfHeightLimited = maxH * (ratio.w / ratio.h);
  const w = Math.min(maxW, wIfHeightLimited);
  const h = w * (ratio.h / ratio.w);

  return {
    width: Math.max(1, Math.floor(w)),
    height: Math.max(1, Math.floor(h)),
    dpr: resolveDpr(opts.maxDpr),
  };
}

/**
 * Apply the resolved viewport: resize the renderer first (so canvas
 * backing store is correct), update the physics world bounds, then ask
 * typography to re-layout against the new dimensions.
 *
 * Order matters: typography layout (corner placement, font sizing)
 * derives from the new render size.
 */
export function applyAspect(
  _aspect: AspectId,
  viewport: AspectViewport,
  adapter: AspectAdapter,
): void {
  adapter.setRenderSize(viewport.width, viewport.height, viewport.dpr);
  adapter.setWorldBounds({ width: viewport.width, height: viewport.height });
  adapter.relayoutTypography();
}
